import React from "react";
import GeniePage from "../../../../components/special/genie-page/GeniePage";
import GenieText from "../../../../components/core/genie-text/GenieText";
import FormControl from "@mui/material/FormControl/FormControl";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Grid,
  Divider,
  Card,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { formatCurrency } from "../../../../utils/currency";
import useUser from "../../../../queries/routes/useUser";
import useCurrentCart from "../../../../queries/routes/useCurrentCart";
import useSubmitOrder from "../../../../queries/routes/useSubmitOrder";
import GenieProcessing from "../../../../components/core/genie-processing/GenieProcessing";
import usePaymentTerms from "../../../../queries/routes/usePaymentTerms";
import useShippingEstimate from "../../../../queries/routes/useShippingEstimate";
import useAddEstimate from "../../../../queries/routes/useAddEstimate";
import { Warning } from "@mui/icons-material";
import useAddShippingName from "../../../../queries/routes/useAddShippingName";
import useOrganization from "../../../../queries/routes/useOrganization";
import StripeCheckout from "../billing/Stripe";

function Billing() {
  const { org_id } = useParams();
  const navigate = useNavigate();

  const { data: user } = useUser();
  const { data: cart } = useCurrentCart(org_id!, user?.organizations[0].id!);
  const { data: organization } = useOrganization(org_id!);

  // Check if Stripe Connect is active
  const isStripeConnectActive = organization?.stripe_connect_status === "active";

  const { mutate: updatePaymentTerms } = usePaymentTerms(
    cart?.order?.id!,
    org_id!,
    user?.organizations[0].id!
  );

  React.useEffect(() => {
    if (cart?.order?.id) {
      updatePaymentTerms({ is_net_30: false });
    }
  }, [cart?.order?.id]);

  const {
    mutate: addShippingEstimateToOrder,
    isLoading: isAddingShippingEstimate,
  } = useAddEstimate(org_id!, user?.organizations[0].id!, cart?.order?.id!);
  const { mutate: addShippingName } = useAddShippingName(
    org_id!,
    user?.organizations[0].id!,
    cart?.order?.id!
  );

  const {
    data: shippingEstimates,
    isLoading: isLoadingEstimates,
    isError: isShippingEstimateError,
  } = useShippingEstimate(cart?.order?.id!);

  const { mutate: submitOrder, isLoading: isSubmitOrderLoading } =
    useSubmitOrder(org_id!, user?.organizations[0].id!, cart?.order?.id!);

  React.useEffect(() => {
    if (
      !isLoadingEstimates &&
      shippingEstimates &&
      Array.isArray(shippingEstimates) &&
      shippingEstimates.length > 0 &&
      !cart?.order?.shippo_service_level_token
    ) {
      const firstEstimate = shippingEstimates[0];
      addShippingEstimateToOrder({
        rate_id: firstEstimate.object_id,
      });
      addShippingName({
        service_level_token: firstEstimate.servicelevel.token,
        provider: firstEstimate.provider,
      });
    }
  }, [
    shippingEstimates,
    cart?.order?.shippo_service_level_token,
    isLoadingEstimates,
    addShippingEstimateToOrder,
    addShippingName,
  ]);

  if (isSubmitOrderLoading) {
    return (
      <GenieProcessing
        phrases={[
          "We are processing your order...",
          "Thanks for placing an order... Happiness awaits!",
        ]}
      />
    );
  }

  return (
    <GeniePage>
      <Box
        mb={2}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <GenieText header>Shipping & Payment</GenieText>
        <GenieText
          link={`/storefront/${org_id}`}
          style={{ cursor: "pointer" }}
          fontSize={"tiny"}
          underline
        >
          Back to store
        </GenieText>
      </Box>
      {cart && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{ p: 3, border: "1px solid", borderColor: "divider" }}
            >
              <Box my={2}>
                <GenieText fontSize={"small"} subdued>
                  Cost of Goods: {formatCurrency(cart.total_cents)}
                </GenieText>
                <GenieText fontSize={"small"} subdued>
                  Shipping:{" "}
                  {cart?.order?.shipping_cost_in_cents
                    ? formatCurrency(cart?.order?.shipping_cost_in_cents)
                    : "Please select shipping"}
                </GenieText>
                <GenieText fontSize={"small"} subdued>
                  Total:{" "}
                  <GenieText fontSize={"small"} fontWeight={"bold"} inline>
                    {formatCurrency(
                      cart.total_cents +
                        (cart?.order?.shipping_cost_in_cents || 0)
                    )}
                  </GenieText>
                </GenieText>
                <Box my={2}>
                  <Divider />
                </Box>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <GenieText fontSize={"small"} style={{ display: "inline" }}>
                    Shipping Options
                  </GenieText>
                  {isAddingShippingEstimate && <CircularProgress size={16} />}
                </Box>
                <Box mb={2} />
                {isLoadingEstimates && (
                  <Box display={"flex"} justifyContent={"center"}>
                    <CircularProgress size={16} />
                  </Box>
                )}

                {(isShippingEstimateError ||
                  shippingEstimates?.length === 0) && (
                  <Alert icon={<Warning />} color={"error"}>
                    There was an error getting shipping estimates. Please try
                    again later.
                  </Alert>
                )}
                {!isLoadingEstimates &&
                  shippingEstimates?.map((estimate) => (
                    <Card
                      key={estimate.object_id}
                      sx={{ p: 1, mb: 1, cursor: "pointer" }}
                      variant={"outlined"}
                      onClick={() => {
                        addShippingEstimateToOrder({
                          rate_id: estimate.object_id,
                        });
                        addShippingName({
                          service_level_token: estimate.servicelevel.token,
                          provider: estimate.provider,
                        });
                      }}
                    >
                      <Box display={"inline-block"} width={"100%"}>
                        <Box display={"flex"} justifyContent={"space-between"}>
                          <Box>
                            <Box display={"flex"}>
                              <Radio
                                checked={
                                  cart?.order?.shippo_service_level_token ===
                                  estimate.servicelevel.token
                                }
                                sx={{ mr: 1 }}
                                size="small"
                                name="radio-buttons"
                                inputProps={{
                                  "aria-label": "Change Shipping for order",
                                }}
                              />
                              <img
                                width={48}
                                height={48}
                                src={estimate.provider_image_200}
                                alt=""
                              />
                              <Box ml={2}>
                                <GenieText fontSize={"small"}>
                                  Carrier: {estimate.provider}
                                </GenieText>
                                <GenieText fontSize={"tiny"}>
                                  {estimate.servicelevel.name}
                                </GenieText>
                              </Box>
                            </Box>
                          </Box>
                          <GenieText fontSize={"tiny"} fontWeight={"bold"}>
                            ${estimate.amount}
                          </GenieText>
                        </Box>
                        {estimate.attributes?.map((attribute) => (
                          <Chip
                            key={attribute}
                            color={"primary"}
                            sx={{ mt: 1, mr: 1 }}
                            size={"small"}
                            variant={"outlined"}
                            label={attribute}
                          />
                        ))}
                      </Box>
                    </Card>
                  ))}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{ p: 2, border: "1px solid", borderColor: "divider" }}
            >
              <StripeCheckout
                submitOrder={submitOrder}
                disabled={!isStripeConnectActive}
                key={`stripe-${
                  cart.total_cents + (cart?.order?.shipping_cost_in_cents || 0)
                }`}
              />
            </Paper>
          </Grid>
        </Grid>
      )}
    </GeniePage>
  );
}

export default Billing;
